import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";
import { toast } from "react-toastify";

function BookList() {
  const [libraryBook, setLibraryBook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchLibraryBookDetails = async () => {
      try {
        const response = await axios.post(
          `${server}/library_data_mongodb`,
          {},
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
            },
          },
        );
        if (response.data && response.data["body-json"]?.body?.data) {
          setLibraryBook(response.data["body-json"].body.data);
        } else {
          console.error("Invalid data format from API", response.data);
        }
      } catch (error) {
        console.error("Error fetching bus details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLibraryBookDetails();
  }, []);

  // Filter
  const filteredRecords = libraryBook.filter((booklist) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(booklist).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (bookIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete book "${bookIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_library_book_mongodb`,
          { bookId: bookIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Library Book Deleted", { position: "top-right" });
          setLibraryBook((prev) =>
            prev.filter((book) => book.bookId !== bookIdToDelete),
          );
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const handleExportAllBooksExcel = () => {
    if (!libraryBook || libraryBook.length === 0) {
      toast.error("No book data to export");
      return;
    }

    const sheetData = libraryBook.map((book) => ({
      "Book ID": book.bookId || "",
      "Book No": book.bookNo || "",
      "Book Type": book.bookType || "",
      Title: book.title || "",
      Subject: book.subject || "",
      Class: book.class || "",
      Language: book.language || "",
      Author: book.author || "",
      Publisher: book.publisher || "",
      "Date Added": book.createdAt
        ? new Date(book.createdAt).toLocaleDateString("en-GB")
        : "",
      "Total Quantity": book.totalQuantity || 0,
      "Available Quantity": book.availableQuantity || 0,
      "Bill No": book.purchase?.billNo || "",
      "Bill Date": book.purchase?.billDate
        ? new Date(book.purchase.billDate).toLocaleDateString("en-GB")
        : "",
      "Purchased From": book.purchase?.purchasedFrom || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Books");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `LibraryBooks_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportBooksClassWiseExcel = () => {
    if (!libraryBook || libraryBook.length === 0) {
      toast.error("No book data to export");
      return;
    }

    const classGroups = {};
    libraryBook.forEach((book) => {
      const cls = book.class || "Unknown_Class";
      if (!classGroups[cls]) classGroups[cls] = [];
      classGroups[cls].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(classGroups).forEach(([className, books]) => {
      const sheetData = books.map((book) => ({
        "Book ID": book.bookId || "",
        "Book No": book.bookNo || "",
        "Book Type": book.bookType || "",
        Title: book.title || "",
        Class: book.class || "",
        Subject: book.subject || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
        "Total Quantity": book.totalQuantity || 0,
        "Available Quantity": book.availableQuantity || 0,
        "Bill No": book.purchase?.billNo || "",
        "Bill Date": book.purchase?.billDate
          ? new Date(book.purchase.billDate).toLocaleDateString("en-GB")
          : "",
        "Purchased From": book.purchase?.purchasedFrom || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = className.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `LibraryBooks_ClassWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportBooksTypeWiseExcel = () => {
    if (!libraryBook || libraryBook.length === 0) {
      toast.error("No book data to export");
      return;
    }

    const typeGroups = {};
    libraryBook.forEach((book) => {
      const type = book.bookType || "Unknown_Type";
      if (!typeGroups[type]) typeGroups[type] = [];
      typeGroups[type].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(typeGroups).forEach(([bookType, books]) => {
      const sheetData = books.map((book) => ({
        "Book ID": book.bookId || "",
        "Book No": book.bookNo || "",
        "Book Type": book.bookType || "",
        Title: book.title || "",
        Class: book.class || "",
        Subject: book.subject || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
        "Total Quantity": book.totalQuantity || 0,
        "Available Quantity": book.availableQuantity || 0,
        "Bill No": book.purchase?.billNo || "",
        "Bill Date": book.purchase?.billDate
          ? new Date(book.purchase.billDate).toLocaleDateString("en-GB")
          : "",
        "Purchased From": book.purchase?.purchasedFrom || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = bookType.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `LibraryBooks_TypeWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportBooksSubjectWiseExcel = () => {
    if (!libraryBook || libraryBook.length === 0) {
      toast.error("No book data to export");
      return;
    }

    const subjectGroups = {};
    libraryBook.forEach((book) => {
      const subject = book.subject || "Unknown_Subject";
      if (!subjectGroups[subject]) subjectGroups[subject] = [];
      subjectGroups[subject].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(subjectGroups).forEach(([subjectName, books]) => {
      const sheetData = books.map((book) => ({
        "Book ID": book.bookId || "",
        "Book No": book.bookNo || "",
        "Book Type": book.bookType || "",
        Title: book.title || "",
        Class: book.class || "",
        Subject: book.subject || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
        "Total Quantity": book.totalQuantity || 0,
        "Available Quantity": book.availableQuantity || 0,
        "Bill No": book.purchase?.billNo || "",
        "Bill Date": book.purchase?.billDate
          ? new Date(book.purchase.billDate).toLocaleDateString("en-GB")
          : "",
        "Purchased From": book.purchase?.purchasedFrom || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = subjectName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `LibraryBooks_SubjectWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportBooksPublisherWiseExcel = () => {
    if (!libraryBook || libraryBook.length === 0) {
      toast.error("No book data to export");
      return;
    }

    const publisherGroups = {};
    libraryBook.forEach((book) => {
      const publisher = book.publisher || "Unknown_Publisher";
      if (!publisherGroups[publisher]) publisherGroups[publisher] = [];
      publisherGroups[publisher].push(book);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(publisherGroups).forEach(([publisherName, books]) => {
      const sheetData = books.map((book) => ({
        "Book ID": book.bookId || "",
        "Book No": book.bookNo || "",
        "Book Type": book.bookType || "",
        Title: book.title || "",
        Class: book.class || "",
        Subject: book.subject || "",
        Language: book.language || "",
        Author: book.author || "",
        Publisher: book.publisher || "",
        "Date Added": book.createdAt
          ? new Date(book.createdAt).toLocaleDateString("en-GB")
          : "",
        "Total Quantity": book.totalQuantity || 0,
        "Available Quantity": book.availableQuantity || 0,
        "Bill No": book.purchase?.billNo || "",
        "Bill Date": book.purchase?.billDate
          ? new Date(book.purchase.billDate).toLocaleDateString("en-GB")
          : "",
        "Purchased From": book.purchase?.purchasedFrom || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = publisherName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `LibraryBooks_PublisherWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((book, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Book ID</span> &mdash;{" "}
                              {book.bookId}
                              <br />
                              <span class="text-dark">
                                Book No
                              </span> &mdash; {book.bookNo}
                              <br />
                              <span class="text-dark">
                                Book Type
                              </span> &mdash; {book.bookType}
                              <br />
                              <span class="text-dark">Title</span> &mdash;{" "}
                              {book.title}
                              <br />
                              <span class="text-dark">
                                Subject
                              </span> &mdash; {book.subject} <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {book.class} <br />
                              <span class="text-dark">
                                Language
                              </span> &mdash; {book.language} <br />
                              <span class="text-dark">Author</span> &mdash;{" "}
                              {book.author}
                              <br />
                              <span class="text-dark">
                                Publisher
                              </span> &mdash; {book.publisher}
                              <br />
                              <span class="text-dark">Date</span> &mdash;{" "}
                              {book.createdAt
                                ? new Date(book.createdAt).toLocaleDateString(
                                    "en-GB",
                                  )
                                : ""}
                              <hr className="w-25" />
                              <span class="text-dark">Total Quantity</span>{" "}
                              &mdash; {book.totalQuantity}
                              <br />
                              <span class="text-dark">
                                Available Quantity
                              </span>{" "}
                              &mdash; {book.availableQuantity}
                              <hr className="w-25" />
                              <span class="text-dark">
                                Bill No
                              </span> &mdash; {book.purchase.billNo}
                              <br />
                              <span class="text-dark">
                                Bill Date
                              </span> &mdash;{" "}
                              {book.purchase.billDate
                                ? new Date(
                                    book.purchase.billDate,
                                  ).toLocaleDateString("en-GB")
                                : ""}
                              <br />
                              <span class="text-dark">Purchased From</span>{" "}
                              &mdash; {book.purchase.purchasedFrom}
                              <br />
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <Link
                                  to="/update-book"
                                  state={{
                                    bookId: book.bookId,
                                  }}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </Link>
                                <span
                                  onClick={() => handleDelete(book.bookId)}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          to="/add-book"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Book
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Library</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Book List
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Book List
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAllBooksExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Books
                                </span>
                                <span
                                  onClick={handleExportBooksClassWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class Wise
                                </span>
                                <span
                                  onClick={handleExportBooksTypeWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Book Type Wise
                                </span>
                                <span
                                  onClick={handleExportBooksSubjectWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Subject Wise
                                </span>
                                <span
                                  onClick={handleExportBooksPublisherWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Publisher Wise
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default BookList;
